import type { AppDataTableColumn } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import {
  AppBadgeForAno,
  AppBadgeForID,
  AppIconButton,
  AppMemberDisplay,
  AppTextLink,
  AppTooltip,
} from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { SalesStageBadge } from '@opportunities/-components/SalesStageBadge'
import { AlertCircle, Copy, ExternalLink } from 'lucide-react'
import { DateTime } from 'luxon'
import { OPPORTUNITY_SALES_STAGE_MAP } from 'shared'
import { formatCurrency } from '@/lib/format-utils'
import type { MonthlySaleWithPeriod } from '../-hooks/useMemberMonthlySalesWithPeriod'

/**
 * 金額差異警告アイコン
 *
 * 売上金額とオポ金額の比率をチェックし、以下の条件で警告を表示します：
 * - オポ金額 ÷ 売上金額 <= 0.03（3%以下）の場合: 「売上金額に対してオポ金額が過少です」
 * - オポ金額 ÷ 売上金額 > 1（100%超）の場合: 「売上金額に対してオポ金額が過剰です」
 *
 * @param salesAmount - 売上金額
 * @param opportunityAmount - オポ金額
 */
function AmountDifferenceWarning({
  salesAmount,
  opportunityAmount,
}: {
  salesAmount: number
  opportunityAmount: number | null
}) {
  if (salesAmount === 0) return null

  // 比率を計算: オポ金額 ÷ 売上金額（nullの場合は0として計算）
  const amountDifferenceRate = (opportunityAmount || 0) / salesAmount

  // 警告条件: 3%以下 または 100%超
  const isWarning = amountDifferenceRate <= 0.03 || amountDifferenceRate > 1

  if (!isWarning) return null

  const warningMessage =
    amountDifferenceRate <= 0.03 ? '売上金額に対してオポ金額が過少です。' : '売上金額に対してオポ金額が過剰です。'

  return (
    <AppTooltip
      content={
        <p className='text-xs'>
          {warningMessage}
          <br />
          営業、サービスマネージャまたはキャプテンポータルにてお問い合わせください。
        </p>
      }
    >
      <AlertCircle className='w-4 h-4 text-yellow-500 shrink-0' />
    </AppTooltip>
  )
}

/**
 * マイページの売上一覧テーブルのカラム定義を生成
 * @param copyToClipboard - クリップボードコピー関数
 * @returns カラム定義の配列
 */
export function createMypageColumns(
  copyToClipboard: (item: MonthlySaleWithPeriod) => void,
): AppDataTableColumn<MonthlySaleWithPeriod>[] {
  return [
    {
      key: 'opportunity_id',
      label: 'オポチュニティ管理ID',
      accessor: (row) => row.opportunity_id,
      render: (row) => <AppBadgeForID value={row.opportunity_id} />,
      cellClassName: 'text-center',
      footer: () => null,
      required: true,
    },
    {
      key: 'sales_id',
      label: '売上情報ID',
      accessor: (row) => row.sales_id,
      render: (row) => <AppBadgeForID value={row.sales_id} />,
      footer: () => null,
      required: true,
    },
    {
      key: 'customer_name',
      label: '取引先名',
      accessor: (row) => row.salesforce_customer_name,
      footer: () => null,
      required: true,
    },
    {
      key: 'ano',
      label: 'ANO',
      accessor: (row) => row.ano,
      sortable: false,
      cellClassName: 'min-w-[100px]',
      headerClassName: 'min-w-[100px]',
      active: true,
      render: (row) => <AppBadgeForAno ano={row.ano} />,
    },
    {
      key: 'salesforce_project_name',
      label: 'ANOの案件名',
      accessor: (row) => row.salesforce_project_name,
      cellClassName: 'w-[250px]',
      headerClassName: 'w-[250px]',
      footer: () => null,
      active: true,
    },
    {
      key: 'project_name',
      label: '売上情報の案件名',
      accessor: (row) => row.salesforce_original_project_name,
      cellClassName: 'w-[250px]',
      headerClassName: 'w-[250px]',
      footer: () => null,
      active: true,
    },
    {
      key: 'sales_stage',
      label: 'Sales Stage',
      accessor: (row) => (row.sales_stage_id ? (OPPORTUNITY_SALES_STAGE_MAP[row.sales_stage_id]?.text ?? '') : ''),
      render: (row) => <SalesStageBadge stageId={row.sales_stage_id} variant='opportunity' />,
      cellClassName: 'w-[80px] text-center',
      headerClassName: 'w-[80px] text-center',
      footer: () => null,
      active: true,
    },
    {
      key: 'previously_closed_sales_stage',
      label: '失注前Sales Stage',
      accessor: (row) =>
        row.previously_closed_sales_stage_id
          ? (OPPORTUNITY_SALES_STAGE_MAP[row.previously_closed_sales_stage_id]?.text ?? '')
          : '',
      render: (row) => <SalesStageBadge stageId={row.previously_closed_sales_stage_id} variant='opportunity' />,
      cellClassName: 'w-[80px] text-center',
      headerClassName: 'w-[80px] text-center',
      footer: () => null,
      active: true,
    },
    {
      key: 'opportunity_amount',
      label: 'オポ金額',
      accessor: (row) => row.opportunity_amount,
      render: (row, value) => (
        <div className='flex items-center justify-end gap-2'>
          <span className='text-right'>{formatCurrency(value as number | null)}</span>
          <AmountDifferenceWarning salesAmount={row.amount} opportunityAmount={row.opportunity_amount} />
        </div>
      ),
      footer: (sales) => {
        const total = sales.reduce((sum, item) => sum + (item.opportunity_amount || 0), 0)
        return <span className='text-right text-xs'>合計: {formatCurrency(total)}</span>
      },
      cellClassName: 'text-right',
      headerClassName: 'text-right',
      footerClassName: 'text-right',
      active: true,
    },
    {
      key: 'amount',
      label: '売上金額',
      accessor: (row) => row.amount,
      render: (_row, value) => <span className='text-right'>{formatCurrency(value as number)}</span>,
      footer: (sales) => {
        const total = sales.reduce((sum, item) => sum + (item.amount || 0), 0)
        return <span className='text-right text-xs'>合計: {formatCurrency(total)}</span>
      },
      cellClassName: 'text-right',
      headerClassName: 'text-right',
      footerClassName: 'text-right',
      active: true,
    },
    {
      key: 'sales_on',
      label: '売上月',
      accessor: (row) => row.sales_on,
      render: (_row, value) => {
        if (!value) return '-'
        // sales_on は 'YYYY-MM' 形式の文字列
        const dt = DateTime.fromFormat(value as string, 'yyyy-MM')
        return dt.isValid ? dt.toFormat('yyyy/MM') : '-'
      },
      footer: () => null,
      active: true,
    },
    {
      key: 'owner',
      label: '売上情報所有者',
      accessor: (row) => row.sales_information_owner_member_name,
      render: (row) => (
        <AppMemberDisplay
          employeeNumber={row.sales_information_owner_member_employee_number}
          name={row.sales_information_owner_member_name}
          emptyDisplay='dash'
        />
      ),
      footer: () => null,
      active: true,
    },
    {
      key: 'link',
      label: 'Link',
      accessor: (row) => row.salesforce_system_id,
      render: (_row, value) =>
        value ? (
          <AppTextLink
            href={`https://shiftinc.my.salesforce.com/${value}`}
            external
            className='flex justify-center'
            aria-label='Salesforceで開く'
          >
            <ExternalLink className='w-4 h-4' />
          </AppTextLink>
        ) : (
          '-'
        ),
      sortable: false,
      cellClassName: 'w-[60px] text-center',
      headerClassName: 'w-[60px] text-center',
      footer: () => null,
      active: true,
    },
    {
      key: 'action',
      label: 'Action',
      accessor: (_row) => null,
      render: (row) => (
        <AppIconButton onClick={() => copyToClipboard(row)} description='CSV形式で行をクリップボードにコピー'>
          <Copy />
        </AppIconButton>
      ),
      sortable: false,
      cellClassName: 'w-[60px] text-center',
      headerClassName: 'w-[60px] text-center',
      footer: () => null,
      required: true,
    },
  ]
}
